/*
 * Two carousels behind one set of arrows, plus the gallery's mobile cut-off.
 *
 * Desktop drives the track with a transform so the 400ms easing in the design
 * is exact. Mobile and tablet leave the track as a native scroll-snap scroller —
 * momentum and rubber-banding stay native — and the arrows, which the design
 * only draws at desktop, are hidden by CSS at those widths. The mode flips at
 * the same 1024px breakpoint the CSS uses; whichever mode is inactive gets its
 * state cleared so the two never stack up.
 */
class StandaloneTestimonials extends HTMLElement {
  connectedCallback() {
    this.setUpCarousel();
    this.setUpShowMore();
  }

  disconnectedCallback() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onRefresh);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.moreButton) this.moreButton.removeEventListener('click', this.onMore);
  }

  setUpCarousel() {
    this.track = this.querySelector('[data-tst-track]');
    this.prevButton = this.querySelector('[data-tst-prev]');
    this.nextButton = this.querySelector('[data-tst-next]');
    this.slides = this.track ? Array.from(this.track.children) : [];

    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.scrollFrame = null;
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onPrev = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.onScroll = () => this.queueScrollSync();
    this.onRefresh = () => this.refresh();

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.desktopQuery.addEventListener('change', this.onRefresh);

    // Watches the track rather than window resize so the theme editor adding or
    // removing a testimonial re-measures too.
    this.resizeObserver = new ResizeObserver(this.onRefresh);
    this.resizeObserver.observe(this.track);

    this.refresh();
  }

  /* One card plus one gap — the distance a single arrow press travels. Measured
     rather than hard-coded because every breakpoint sizes cards off the track. */
  get step() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.slides[0].getBoundingClientRect().width + gap;
  }

  /* How far the rail can travel before the last card is flush with the right
     edge. Resolves to 0 on desktop while three cards fill the 1240 measure,
     which is what leaves both arrows disabled at the drawn width — the rail only
     moves once a fourth testimonial is added. */
  get maxIndex() {
    const step = this.step;
    if (step <= 0) return 0;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const visible = Math.max(1, Math.round((this.track.clientWidth + gap) / step));
    return Math.max(0, this.slides.length - visible);
  }

  move(direction) {
    this.goTo(this.index + direction);
  }

  goTo(index) {
    const target = Math.min(Math.max(index, 0), this.maxIndex);
    this.index = target;

    if (this.desktopQuery.matches) {
      this.track.style.transform = `translate3d(${-target * this.step}px, 0, 0)`;
    } else {
      this.track.scrollTo({
        left: target * this.step,
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
      });
    }

    this.updateButtons();
  }

  /* Swiping moves the mobile rail without going through goTo, so the index is
     read back off the scroller to keep the arrows honest when the width flips. */
  queueScrollSync() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (this.desktopQuery.matches) return;

      const step = this.step;
      if (step <= 0) return;

      this.index = Math.min(Math.round(this.track.scrollLeft / step), this.maxIndex);
      this.updateButtons();
    });
  }

  refresh() {
    this.index = Math.min(this.index, this.maxIndex);

    if (this.desktopQuery.matches) {
      // Any scroll offset left over from mobile would compound with the transform.
      this.track.scrollLeft = 0;
      this.track.style.transform = `translate3d(${-this.index * this.step}px, 0, 0)`;
    } else {
      this.track.style.transform = '';
    }

    this.updateButtons();
  }

  updateButtons() {
    const max = this.maxIndex;
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= max;
  }

  /* The gallery ships nearly 3000px of photos on a phone, so the tail is held
     back until asked for. One-way on purpose: having scrolled through them,
     collapsing the stack would throw the reader back up the page. */
  setUpShowMore() {
    this.grid = this.querySelector('[data-tst-grid]');
    this.moreButton = this.querySelector('[data-tst-more]');

    if (!this.grid || !this.moreButton) return;

    this.onMore = () => {
      this.grid.setAttribute('data-tst-expanded', '');
      this.moreButton.setAttribute('aria-expanded', 'true');
      this.moreButton.hidden = true;
    };

    this.moreButton.addEventListener('click', this.onMore);
  }
}

customElements.define('standalone-testimonials', StandaloneTestimonials);
